"use client";

import { AuthPageWrapper } from "@/components/auth-page-wrapper";
import { SessionLoadingWrapper } from "@/components/session-loading-wrapper";
import { TourProvider, TourWrapper } from "@/components/tour";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CompanyAccessDeniedProvider } from "@/context/CompanyAccessDeniedContext";
import { DatabaseProvider } from "@/context/DatabaseContext";
import { DrawerProvider } from "@/context/DrawerContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import { PeopleProvider } from "@/context/peopleContext";
import { SentryProvider } from "@/context/SentryContext";
import { SessionProvider } from "@/context/SessionContext";
import { ToastProvider } from "@/context/ToastContext";
import {
  isCompanyAccessDeniedError,
  notifyCompanyAccessDenied,
} from "@/utils/errors/companyAccessDenied";
import ThemeProvider from "@/utils/themeProvider";
import { QueryPerfOverlay } from "@/components/debug/query-perf-overlay";
import { installQueryPerfTracking } from "@/utils/perf/queryPerf";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { usePathname, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { I18nextProvider } from "react-i18next";
import i18n from "../utils/i18n";

// Dev-only React Query devtools, lazily loaded so it never ships to production.
const ReactQueryDevtools = dynamic(
  () =>
    import("@tanstack/react-query-devtools").then(
      (mod) => mod.ReactQueryDevtools,
    ),
  { ssr: false },
);

const PUBLIC_WEBSITE_PATHS = [
  "/",
  "/features",
  "/pricing",
  "/faq",
  "/cookies",
  "/dpa",
  "/privacy",
  "/terms",
  "/coming-soon",
  "/priority-club",
  "/thank-you",
  "/services",
  "/info",
  "/monitoring",
  "/docs",
];

const isPublicWebsitePath = (pathname: string) => {
  return PUBLIC_WEBSITE_PATHS.some((path) =>
    path === "/" ? pathname === "/" : pathname.startsWith(path),
  );
};

// Mobile app (/mobile) has its own JWT/session system
// (getMobileSession, employee-typed cookie) — it doesn't use Supabase Auth at
// all. The admin provider tree below (SessionProvider, PeopleProvider,
// DrawerProvider, NotificationsProvider, TourProvider, AuthPageWrapper,
// SessionLoadingWrapper) assumes an admin session and gates rendering on it;
// SessionLoadingWrapper in particular treats any non-public path as
// "signed-in, requires auth" and would show an endless loading spinner for an
// employee who will never have an admin session. So /mobile takes the same
// lightweight branch as the public website.
const MOBILE_APP_PATH_PREFIX = "/mobile";

const shouldUseLightweightProviders = (pathname: string) => {
  return isPublicWebsitePath(pathname) || pathname.startsWith(MOBILE_APP_PATH_PREFIX);
};

export function ClientProviders({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            if (isCompanyAccessDeniedError(error)) {
              notifyCompanyAccessDenied(error);
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            if (isCompanyAccessDeniedError(error)) {
              notifyCompanyAccessDenied(error);
            }
          },
        }),
      }),
  );
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const shouldSkipAuthProviders = shouldUseLightweightProviders(pathname || "");
  const isDebug = searchParams?.get("debug") === "true";

  useEffect(() => {
    if (
      process.env.NODE_ENV !== "development" &&
      process.env.NEXT_PUBLIC_PERF_DEBUG !== "true"
    ) {
      return;
    }
    return installQueryPerfTracking(queryClient);
  }, [queryClient]);

  const content = shouldSkipAuthProviders ? (
    <ThemeProvider>
      <TooltipProvider delayDuration={0}>
        <ToastProvider>{children}</ToastProvider>
      </TooltipProvider>
    </ThemeProvider>
  ) : (
    <DatabaseProvider>
      <DrawerProvider>
        <ToastProvider>
          <SessionProvider>
            <CompanyAccessDeniedProvider>
              <NotificationsProvider>
                <TourProvider>
                  <AuthPageWrapper>
                    <SessionLoadingWrapper>
                      <PeopleProvider>
                        <ThemeProvider>
                          <TooltipProvider delayDuration={0}>
                            <TourWrapper>{children}</TourWrapper>
                          </TooltipProvider>
                        </ThemeProvider>
                      </PeopleProvider>
                    </SessionLoadingWrapper>
                  </AuthPageWrapper>
                </TourProvider>
              </NotificationsProvider>
            </CompanyAccessDeniedProvider>
          </SessionProvider>
        </ToastProvider>
      </DrawerProvider>
    </DatabaseProvider>
  );

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <React.StrictMode>
          <SentryProvider>{content}</SentryProvider>
        </React.StrictMode>
        <QueryPerfOverlay />
        {isDebug ? <ReactQueryDevtools initialIsOpen={false} /> : null}
      </QueryClientProvider>
    </I18nextProvider>
  );
}
